import { useCallback, useEffect, useRef, useState } from 'react';

import {
  MAX_EVENTS,
  MAX_TOPICS,
  hexColorSchema,
  randomTopicColor,
  type EventResponse,
  type ReorderEventsRequest,
  type TimelineResponse,
  type TopicResponse,
} from '@lifetimeline/shared';

import { Button } from '../components/Button.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { Field } from '../components/Field.js';
import { Notice } from '../components/Notice.js';
import { PlusIcon } from '../components/icons.js';
import { Sheet } from '../components/Sheet.js';
import { UpgradeDialog } from '../components/UpgradeDialog.js';
import { Spinner } from '../components/Spinner.js';
import { SortableTopicChip, SortableTopicRow } from '../components/SortableTopicRow.js';
import { Timeline } from '../components/Timeline.js';
import { useToast } from '../components/Toast.js';
import { useI18n } from '../i18n/index.js';
import { LIMIT_VALUES, api, errorMessageKey } from '../lib/apiClient.js';
import styles from './pageStyles.module.css';

/**
 * 내 인생 타임라인 — 적고 고치는 화면.
 *
 * 주제를 먼저 만들고 그 안에 사건을 넣는다. 주제 없이 사건부터 받으면
 * 색도 못 정하고 묶을 축도 없다.
 *
 * 홈의 한 탭으로 들어간다 — 셸(`Layout`)과 로그인 확인은 홈이 한다.
 * 여기서 또 하면 탭을 바꿀 때마다 화면이 통째로 깜빡인다.
 */
export function TimelinePanel() {
  const { t } = useI18n();
  const toast = useToast();

  const [data, setData] = useState<TimelineResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingTopic, setEditingTopic] = useState<TopicResponse | 'new' | null>(null);
  const [editingEvent, setEditingEvent] = useState<EventResponse | 'new' | null>(null);
  const [doomedTopic, setDoomedTopic] = useState<TopicResponse | null>(null);

  const [more, setMore] = useState(false);
  /*
   * 지금까지 읽은 쪽 수. **state가 아니라 ref다** — 이 값이 바뀌었다고 다시
   * 그릴 일이 없고, state로 두면 `load`가 매번 새 함수가 되어 처음 불러오는
   * effect가 다시 돈다(그러면 목록이 또 깜빡인다).
   */
  const pagesRef = useRef(1);
  /* 무료로 되는 데까지 온 순간 — 화면을 옮기지 않고 위에 띄운다 */
  const [upgrade, setUpgrade] = useState(false);

  /**
   * 지금까지 **읽은 만큼** 다시 읽는다.
   *
   * ⚠️ 예전에는 1쪽만 받아 통째로 갈아 끼웠다. 스크롤해서 세 쪽을 보던
   * 사람이 기록 하나를 고치면 목록이 30줄에서 10줄로 **줄어들면서** 화면이
   * 덜컹 올라갔다 — 고친 줄을 보려고 다시 스크롤해 내려가야 했다.
   *
   * 쪽을 다 받은 **뒤에 한 번만** 갈아 끼운다. 중간에 넣으면 받는 동안
   * 목록이 10줄 → 20줄 → 30줄로 자라면서 또 덜컹인다.
   */
  const load = useCallback(async () => {
    try {
      let merged: TimelineResponse | null = null;
      let cursor: string | null = null;
      for (let page = 0; page < pagesRef.current; page += 1) {
        const next: TimelineResponse = await api.get<TimelineResponse>(
          cursor === null
            ? '/api/me/timeline'
            : `/api/me/timeline?cursor=${encodeURIComponent(cursor)}`,
        );
        merged =
          merged === null
            ? next
            : {
                ...next,
                events: [...merged.events, ...next.events],
                /* 전체 개수는 **첫 쪽에만** 온다 — 뒤 쪽의 `null`로 덮지 않는다 */
                eventTotal: merged.eventTotal,
              };
        cursor = next.nextCursor;
        if (cursor === null) {
          break;
        }
      }
      setData(merged);
      setError(null);
    } catch (cause) {
      setError(t(errorMessageKey(cause)));
    }
  }, [t]);

  /**
   * 다음 쪽을 **이어 붙인다.**
   *
   * 커서는 "몇 번째부터"가 아니라 "어디서부터"다 — 보는 동안 앞에서 기록이
   * 생기거나 사라져도 건너뛰거나 두 번 보이지 않는다.
   */
  const loadMore = async () => {
    const cursor = data?.nextCursor;
    if (cursor === undefined || cursor === null || more) {
      return;
    }
    setMore(true);
    try {
      const next = await api.get<TimelineResponse>(
        `/api/me/timeline?cursor=${encodeURIComponent(cursor)}`,
      );
      setData((current) =>
        current === null
          ? next
          : {
              ...current,
              events: [...current.events, ...next.events],
              nextCursor: next.nextCursor,
            },
      );
      /* 다음에 다시 읽을 때 여기까지 읽어 와야 목록이 안 줄어든다 */
      pagesRef.current += 1;
    } catch (cause) {
      toast.show(t(errorMessageKey(cause), LIMIT_VALUES), 'error');
    } finally {
      setMore(false);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * 끌어다 놓은 순서를 저장한다.
   *
   * **화면을 먼저 바꾸고** 서버에 보낸다. 손을 뗀 것이 제자리로 돌아갔다가
   * 잠시 뒤 다시 움직이면 끊겨 보인다. 실패하면 서버 값으로 되돌린다 —
   * 안 되돌리면 화면에는 옮겨졌는데 다음에 열면 원래대로인 상태가 된다.
   */
  const reorder = (years: ReorderEventsRequest['years']) => {
    setData((current) => {
      if (current === null) {
        return current;
      }
      /*
       * 해를 넘겨 끌었으면 자리뿐 아니라 **연도도** 바뀐다. 자리만 고치면
       * 사건이 잠깐 옛 해에 남았다가 서버 응답을 받고서야 건너뛴다.
       */
      const placed = new Map(
        years.flatMap((group) =>
          group.eventIds.map((eventId, index) => [eventId, { year: group.year, position: index }]),
        ),
      );
      return {
        ...current,
        events: current.events.map((event) => {
          const next = placed.get(event.eventId);
          return next === undefined ? event : { ...event, ...next };
        }),
      };
    });

    void api.put('/api/me/timeline/events/order', { years }).catch(async (cause) => {
      toast.show(t(errorMessageKey(cause), LIMIT_VALUES), 'error');
      await load();
    });
  };

  /**
   * 주제 차례 바꾸기. 기록과 **같은 방식**이다 — 화면을 먼저 바꾸고 보낸다.
   *
   * 서버에는 **전체 목록**을 보낸다. 옮긴 것만 보내면 나머지 자리를 서버가
   * 추측해야 하고, 그 추측이 화면과 어긋나면 방금 옮긴 것이 제자리로 돌아간
   * 것처럼 보인다.
   */
  const reorderTopics = async (topicIds: string[]) => {
    setData((current) => {
      if (current === null) {
        return current;
      }
      const byId = new Map(current.topics.map((topic) => [topic.topicId, topic]));
      return {
        ...current,
        topics: topicIds.flatMap((id) => {
          const topic = byId.get(id);
          return topic === undefined ? [] : [topic];
        }),
      };
    });

    try {
      await api.put('/api/me/timeline/topics/order', { topicIds });
    } catch (cause) {
      toast.show(t(errorMessageKey(cause), LIMIT_VALUES), 'error');
      await load();
    }
  };

  const topics = data?.topics ?? [];
  const events = data?.events ?? [];
  /*
   * 상한은 **불러온 것이 아니라 전체**로 본다 — 쪽으로 나뉘어 오므로 화면에
   * 있는 수로 재면 늘 상한 아래로 보인다.
   *
   * ⚠️ 예전에는 "커서가 남아 있으면 상한에 닿은 것"으로 쳤다. 그러면 **22개
   * 가진 사람이 기록을 못 넣는다** — 첫 쪽(10개) 뒤에 커서가 남으니 늘 상한
   * 으로 읽혔다. 지금은 서버가 센 수를 그대로 쓴다.
   */
  const total = data?.eventTotal ?? 0;

  return (
    <>
      {error !== null ? (
        <div className={styles.noticeSlot}>
          <Notice tone="error">{error}</Notice>
        </div>
      ) : null}

      {data === null ? (
        <div className={styles.centered}>
          <Spinner label={t('common.loading')} />
        </div>
      ) : (
        <>
          {/*
            주제 줄. **`전체`가 맨 앞에 선택된 채로** 있다 — 남이 보는
            화면과 같은 모양이어야 해서다. 다만 여기서는 **걸러지지 않는다**:
            걸러 놓으면 그 해의 일부만 화면에 있는데 그 순서를 저장하면 안
            보이는 기록이 자리를 잃는다. 고치는 자리는 늘 전체다.

            더하기는 줄 끝이다 — 주제를 만드는 일이라 주제들 옆이 제자리고,
            따로 떼어 오른쪽 위에 두면 무엇에 대한 버튼인지 한 번 더 생각해야 한다.
          */}
          <SortableTopicRow
            order={topics.map((topic) => topic.topicId)}
            onReorder={(next) => void reorderTopics(next)}
          >
            {/*
              `전체`와 `주제 추가`는 **끌리지 않는다.** 주제가 아니라 자리
              표시라, 끌 수 있게 두면 어디로 옮기든 의미가 없는 동작이 된다.
            */}
            <li>
              <span className={styles.topicChip} aria-current="true" data-fixed="true">
                {t('timeline.all')}
              </span>
            </li>
            {topics.map((topic) => (
              <SortableTopicChip key={topic.topicId} topicId={topic.topicId}>
                <button
                  type="button"
                  className={styles.topicChip}
                  style={{ borderColor: topic.color, color: topic.color }}
                  onClick={() => setEditingTopic(topic)}
                >
                  {topic.label}
                </button>
              </SortableTopicChip>
            ))}
            <li>
              {/*
                상한에 닿아도 **잠그지 않는다.** 누르면 안내가 뜬다 — 여기까지
                쓴 사람은 이미 이 서비스를 쓰고 있는 사람이라, 벽을 세우는
                것보다 길을 내는 편이 맞다.
              */}
              <button
                type="button"
                className={styles.topicAdd}
                onClick={() =>
                  topics.length >= MAX_TOPICS ? setUpgrade(true) : setEditingTopic('new')
                }
              >
                <PlusIcon size={13} />
                {t('timeline.addTopic')}
              </button>
            </li>
          </SortableTopicRow>

          {topics.length === 0 ? (
            /*
              주제가 없어도 **같은 뼈대**를 보여준다. 점선 상자에 큰 버튼
              하나를 띄우면, 주제를 만드는 순간 화면이 통째로 다른 것으로
              바뀐다 — 처음 한 번 배운 자리가 곧바로 무효가 된다.
              지금은 빈 축이 그대로 있고 거기에 기록이 쌓일 뿐이다.
            */
            <Timeline
              topics={[]}
              events={[]}
              before={
                <button
                  type="button"
                  className={styles.addEvent}
                  onClick={() => setEditingTopic('new')}
                >
                  <PlusIcon size={13} />
                  {t('timeline.addTopic')}
                </button>
              }
            />
          ) : (
            <>
              {/*
                기록은 **축 위에 작게** 붙인다. 화면 폭을 다 쓰는 큰 버튼은
                타임라인보다 무거워져서, 정작 읽으러 온 것을 밀어낸다.
              */}
              <Timeline
                topics={topics}
                events={events}
                onEventClick={setEditingEvent}
                onReorder={reorder}
                {...(data?.nextCursor === null || data === null
                  ? {}
                  : { onLoadMore: () => void loadMore(), loadingMore: more })}
                before={
                  <button
                    type="button"
                    className={styles.addEvent}
                    onClick={() =>
                      total >= MAX_EVENTS ? setUpgrade(true) : setEditingEvent('new')
                    }
                  >
                    <PlusIcon size={13} />
                    {t('timeline.addEvent')}
                  </button>
                }
              />
            </>
          )}
        </>
      )}

      <Sheet
        open={editingTopic !== null}
        onClose={() => setEditingTopic(null)}
        title={editingTopic === 'new' ? t('timeline.addTopic') : t('timeline.editTopic')}
      >
        {editingTopic !== null ? (
          <TopicForm
            topic={editingTopic === 'new' ? null : editingTopic}
            onDone={() => {
              setEditingTopic(null);
              void load();
            }}
            onDelete={(topic) => {
              setEditingTopic(null);
              setDoomedTopic(topic);
            }}
          />
        ) : null}
      </Sheet>

      <Sheet
        open={editingEvent !== null}
        onClose={() => setEditingEvent(null)}
        title={editingEvent === 'new' ? t('timeline.addEvent') : t('timeline.editEvent')}
      >
        {editingEvent !== null ? (
          <EventForm
            topics={topics}
            event={editingEvent === 'new' ? null : editingEvent}
            onDone={() => {
              setEditingEvent(null);
              void load();
            }}
          />
        ) : null}
      </Sheet>

      <UpgradeDialog open={upgrade} onClose={() => setUpgrade(false)} />

      {/*
        주제를 지우면 그 사건도 같이 사라진다. 몇 개가 사라지는지 숫자로
        말해 주지 않으면 무엇을 잃는지 모르고 누른다.
      */}
      <ConfirmDialog
        open={doomedTopic !== null}
        destructive
        confirmLabel={t('common.delete')}
        title={t('timeline.deleteTopicConfirm', { label: doomedTopic?.label ?? '' })}
        body={t('timeline.deleteTopicBody', {
          count: events.filter((event) => event.topicId === doomedTopic?.topicId).length,
        })}
        onCancel={() => setDoomedTopic(null)}
        onConfirm={async () => {
          const target = doomedTopic;
          setDoomedTopic(null);
          if (target === null) {
            return;
          }
          try {
            await api.delete(`/api/me/timeline/topics/${target.topicId}`);
            toast.show(t('timeline.topicDeleted'));
            await load();
          } catch (cause) {
            toast.show(t(errorMessageKey(cause), LIMIT_VALUES), 'error');
          }
        }}
      />
    </>
  );
}

function TopicForm({
  topic,
  onDone,
  onDelete,
}: {
  topic: TopicResponse | null;
  onDone: () => void;
  onDelete: (topic: TopicResponse) => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [label, setLabel] = useState(topic?.label ?? '');
  /*
   * 새로 만들 때는 **무작위로 하나 채워 둔다.** 빈 색 칸 앞에서 "무슨 색을
   * 고르지" 하고 멈추게 하지 않으려는 것이다. 마음에 안 들면 바꾸면 된다.
   *
   * `useState`의 초깃값으로 부르므로 첫 렌더에서 한 번만 정해진다 — 매
   * 렌더마다 부르면 글자를 칠 때마다 색이 바뀐다.
   */
  const [color, setColor] = useState<string>(() => topic?.color ?? randomTopicColor());
  const [busy, setBusy] = useState(false);
  const validColor = hexColorSchema.safeParse(color).success;

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        setBusy(true);
        try {
          await api.post('/api/me/timeline/topics', {
            ...(topic === null ? {} : { topicId: topic.topicId }),
            label: label.trim(),
            color,
          });
          toast.show(t('picks.saved'));
          onDone();
        } catch (cause) {
          toast.show(t(errorMessageKey(cause), LIMIT_VALUES), 'error');
        } finally {
          setBusy(false);
        }
      }}
    >
      <Field
        label={t('timeline.topicLabel')}
        value={label}
        maxLength={30}
        autoFocus={topic === null}
        onChange={(event) => setLabel(event.target.value)}
      />

      <span className={styles.fieldLabel}>{t('timeline.topicColor')}</span>

      {/*
        한 줄에 **견본과 코드**뿐이다. 견본을 누르면 OS 색 고르개가 열리고,
        칸에는 코드를 직접 적는다.

        견본 동그라미를 늘어놓지 않는다 — 여덟 개를 깔면 그것이 고를 수 있는
        전부처럼 보이고, 아홉 번째 주제부터는 남의 색을 쓰게 된다. 정해 둔
        색은 **새로 만들 때 무작위로 채워 주는 기본값**으로만 쓴다.
      */}
      <div className={styles.colorPick}>
        <input
          type="color"
          className={styles.colorWell}
          value={validColor ? color : '#000000'}
          aria-label={t('timeline.topicColor')}
          onChange={(event) => setColor(event.target.value)}
        />
        <input
          type="text"
          className={styles.colorCode}
          value={color}
          maxLength={7}
          spellCheck={false}
          autoCapitalize="none"
          autoCorrect="off"
          aria-label={t('timeline.colorCode')}
          aria-invalid={!validColor || undefined}
          onChange={(event) => setColor(event.target.value.trim().toLowerCase())}
        />
      </div>
      {color !== '' && !validColor ? (
        <p className={styles.colorError}>{t('timeline.colorInvalid')}</p>
      ) : null}

      <Button type="submit" fullWidth loading={busy} disabled={label.trim() === '' || !validColor}>
        {t('common.save')}
      </Button>

      {topic !== null ? (
        <div className={styles.danger}>
          <Button type="button" variant="danger" fullWidth onClick={() => onDelete(topic)}>
            {t('common.delete')}
          </Button>
        </div>
      ) : null}
    </form>
  );
}

/**
 * 고를 수 있는 연도.
 *
 * **1940년부터 내년까지.** 처음에는 1900년부터 뒀는데 선택지가 128개가 되어
 * 옛날로 가려면 한참을 굴려야 했다. 인생을 적는 곳이라 1940년 이전을 고를
 * 사람은 사실상 없다 — 서버는 여전히 1900년까지 받으므로(`yearSchema`)
 * 필요해지면 여기만 늘리면 된다.
 *
 * 내년까지 두는 이유: 이직·입학처럼 **정해졌지만 아직 안 온** 일을 적는
 * 사람이 있다.
 *
 * 최근이 위다. 인생 기록은 옛날보다 요즘을 더 자주 적는다.
 */
const FIRST_YEAR = 1940;
const LAST_YEAR = new Date().getFullYear() + 1;
const YEARS = Array.from({ length: LAST_YEAR - FIRST_YEAR + 1 }, (_, i) => String(LAST_YEAR - i));

const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1));

function EventForm({
  topics,
  event,
  onDone,
}: {
  topics: readonly TopicResponse[];
  event: EventResponse | null;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [topicId, setTopicId] = useState(event?.topicId ?? topics[0]?.topicId ?? '');
  const [year, setYear] = useState(String(event?.year ?? new Date().getFullYear()));
  const [title, setTitle] = useState(event?.title ?? '');
  const [note, setNote] = useState(event?.note ?? '');
  const [startMonth, setStartMonth] = useState(String(event?.startMonth ?? ''));
  const [endYear, setEndYear] = useState(String(event?.endYear ?? ''));
  const [endMonth, setEndMonth] = useState(String(event?.endMonth ?? ''));
  const [url, setUrl] = useState(event?.url ?? '');
  /*
   * 기간을 쓸 것인가.
   *
   * 기본은 **연도 하나**다. 인생을 훑는 축이라 "2019년"이면 충분한 기록이
   * 대부분이고, 달까지 물으면 기억나지 않는 값을 지어내게 된다. 기간이
   * 필요한 사람만 켠다.
   */
  const [ranged, setRanged] = useState(event?.startMonth !== undefined);
  const [busy, setBusy] = useState(false);

  const yearNumber = Number(year);

  /*
   * 빈 칸은 "안 적었다"다. `Number('')`가 0이라 그냥 넘기면 0월이 된다.
   */
  const asNumber = (value: string): number | undefined =>
    value.trim() === '' ? undefined : Number(value);

  const startMonthNumber = asNumber(startMonth);
  const endYearNumber = asNumber(endYear);
  const endMonthNumber = asNumber(endMonth);

  /* 끝은 연도와 달이 함께여야 한다 — select가 짝을 맞춰 주지만 저장 전에 한 번 더 본다 */
  const endComplete = (endYearNumber === undefined) === (endMonthNumber === undefined);
  const endAfterStart =
    endYearNumber === undefined ||
    endMonthNumber === undefined ||
    startMonthNumber === undefined ||
    endYearNumber * 12 + endMonthNumber >= yearNumber * 12 + startMonthNumber;
  const validUrl = url.trim() === '' || url.trim().startsWith('https://');

  return (
    <form
      onSubmit={async (submitEvent) => {
        submitEvent.preventDefault();
        setBusy(true);
        try {
          await api.post('/api/me/timeline/events', {
            ...(event === null ? {} : { eventId: event.eventId }),
            topicId,
            year: yearNumber,
            title: title.trim(),
            ...(note.trim() === '' ? {} : { note: note.trim() }),
            ...(startMonthNumber === undefined ? {} : { startMonth: startMonthNumber }),
            ...(endYearNumber === undefined ? {} : { endYear: endYearNumber }),
            ...(endMonthNumber === undefined ? {} : { endMonth: endMonthNumber }),
            ...(url.trim() === '' ? {} : { url: url.trim() }),
          });
          toast.show(t('picks.saved'));
          onDone();
        } catch (cause) {
          toast.show(t(errorMessageKey(cause), LIMIT_VALUES), 'error');
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className={styles.dateRows}>
        {/*
          기간을 켜고 끄는 자리. **맨 위**에 둔다 — 아래 줄이 무엇인지를
          먼저 말해 줘야 연도 하나만 있는 줄을 보고 "달은 어디 갔지"를
          묻지 않는다.
        */}
        <button
          type="button"
          className={styles.switchRow}
          role="switch"
          aria-checked={ranged}
          onClick={() => {
            const next = !ranged;
            setRanged(next);
            if (next) {
              // 켜는 순간 달이 비어 있으면 종료를 못 누른다 — 1월로 채워 둔다
              if (startMonth === '') {
                setStartMonth('1');
              }
            } else {
              // 끄면 기간이 없는 기록이 된다. 남겨 두면 안 보이는 값이 저장된다
              setStartMonth('');
              setEndYear('');
              setEndMonth('');
            }
          }}
        >
          <span className={styles.switchLabel}>{t('timeline.usePeriod')}</span>
          <span className={styles.switchTrack} data-on={ranged}>
            <span className={styles.switchKnob} />
          </span>
        </button>

        <div className={styles.dateRow}>
          <span className={styles.dateLabel}>
            {ranged ? t('timeline.eventYear') : t('timeline.eventYearOnly')}
          </span>
          <div className={styles.dateValue}>
            <select
              className={styles.select}
              value={year}
              aria-label={t('timeline.eventYear')}
              onChange={(changed) => setYear(changed.target.value)}
            >
              {YEARS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {!ranged ? null : (
              <select
                className={styles.select}
                value={startMonth}
                aria-label={t('timeline.monthNone')}
                onChange={(changed) => setStartMonth(changed.target.value)}
              >
                {MONTHS.map((month) => (
                  <option key={month} value={month}>
                    {t('timeline.monthUnit', { month })}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {!ranged ? null : (
          <div className={styles.dateRow}>
            <span className={styles.dateLabel}>{t('timeline.eventEnd')}</span>
            <div className={styles.dateValue}>
              <select
                className={styles.select}
                value={endYear}
                aria-label={t('timeline.eventEnd')}
                onChange={(changed) => {
                  setEndYear(changed.target.value);
                  /*
                   * 끝은 연·월이 **같이** 있어야 한다(스키마). 연도를 고르는
                   * 순간 달을 시작 달로 채워 둔다 — 안 그러면 저장 버튼이
                   * 꺼져 있는데 왜인지가 화면에 안 보인다.
                   */
                  if (changed.target.value === '') {
                    setEndMonth('');
                  } else if (endMonth === '') {
                    setEndMonth(startMonth === '' ? '1' : startMonth);
                  }
                }}
              >
                <option value="">{t('timeline.endNone')}</option>
                {YEARS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <select
                className={styles.select}
                value={endMonth}
                disabled={endYear === ''}
                aria-label={t('timeline.monthNone')}
                onChange={(changed) => setEndMonth(changed.target.value)}
              >
                <option value="">{t('timeline.monthNone')}</option>
                {MONTHS.map((month) => (
                  <option key={month} value={month}>
                    {t('timeline.monthUnit', { month })}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
      {ranged && !endAfterStart ? (
        <p className={styles.fieldError}>{t('timeline.endBeforeStart')}</p>
      ) : null}

      {/*
        **언제 → 어느 줄기 → 무슨 일.**

        시간이 먼저인 이유: 이 서비스의 축이 연도다. 기록이 어디에 꽂히는지가
        정해진 뒤에 내용을 적는 편이 자연스럽다. 주제가 가운데인 것은 시간과
        내용 사이의 다리라서다 — 색이 곧 축에 찍히는 점이 된다.
      */}
      <div className={styles.dateRows}>
        <div className={styles.dateRow}>
          <span className={styles.dateLabel}>{t('timeline.eventTopic')}</span>
          {/* 칩은 그대로 둔다 — 주제의 **색**이 곧 축에 찍히는 점의 색이다 */}
          <ul className={styles.topicRow}>
            {topics.map((topic) => (
              <li key={topic.topicId}>
                <button
                  type="button"
                  className={styles.topicChip}
                  style={{
                    borderColor: topic.color,
                    color: topicId === topic.topicId ? 'var(--bg)' : topic.color,
                    background: topicId === topic.topicId ? topic.color : 'transparent',
                  }}
                  aria-pressed={topicId === topic.topicId}
                  onClick={() => setTopicId(topic.topicId)}
                >
                  {topic.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/*
        라벨을 칸 위에 두지 않고 **칸 안의 플레이스홀더**로 넣는다. 줄마다
        라벨 한 줄씩이 붙으면 칸 셋에 여섯 줄이 되는데, 그 라벨들이 하는 말은
        칸이 비었을 때만 필요하다.
      */}
      <div className={styles.dateRows}>
        <div className={styles.textRow}>
          <input
            className={styles.textInput}
            value={title}
            placeholder={t('timeline.eventTitle')}
            aria-label={t('timeline.eventTitle')}
            maxLength={80}
            autoFocus={event === null}
            onChange={(changed) => setTitle(changed.target.value)}
          />
        </div>
        <div className={styles.textRow}>
          <input
            className={styles.textInput}
            value={note}
            placeholder={t('timeline.eventNote')}
            aria-label={t('timeline.eventNote')}
            maxLength={200}
            onChange={(changed) => setNote(changed.target.value)}
          />
        </div>
        <div className={styles.textRow}>
          <input
            className={styles.textInput}
            value={url}
            placeholder={t('timeline.eventUrl')}
            aria-label={t('timeline.eventUrl')}
            maxLength={300}
            inputMode="url"
            data-invalid={!validUrl}
            onChange={(changed) => setUrl(changed.target.value)}
          />
        </div>
      </div>
      {!validUrl ? <p className={styles.fieldError}>{t('timeline.urlInvalid')}</p> : null}

      <Button
        type="submit"
        fullWidth
        loading={busy}
        disabled={
          title.trim() === '' || topicId === '' || !endComplete || !endAfterStart || !validUrl
        }
      >
        {t('common.save')}
      </Button>

      {event !== null ? (
        <div className={styles.danger}>
          <Button
            type="button"
            variant="danger"
            fullWidth
            onClick={async () => {
              try {
                await api.delete(`/api/me/timeline/events/${event.eventId}`);
                toast.show(t('timeline.eventDeleted'));
                onDone();
              } catch (cause) {
                toast.show(t(errorMessageKey(cause), LIMIT_VALUES), 'error');
              }
            }}
          >
            {t('common.delete')}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
